import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react';

import { useSessionStorage } from 'react-use';

import { TabList } from '../../../../compositions';
import { ConnectedLoginForm } from '../../../../connectors';
import { Customer } from '../../../../entities/@account/Customer/Customer';
import { CheckoutDetailsFormData, checkoutDetailsTabKey } from '../../../../entities/@forms/Checkout/Checkout';
import { Tab } from '../../../../entities/@navigation/Tab/Tab';
import { CheckoutDetailsTab } from '../../../../entities/@shop/Checkout/Checkout';
import { useTrans } from '../../../../hooks';
import { CheckoutDetailsForm } from '../../../@forms/CheckoutDetailsForm/CheckoutDetailsForm';

import './DetailsStep.scss';

interface DetailsStepProps {
    customer?: Customer;
    onSubmit: (formData: CheckoutDetailsFormData) => void;
    className?: string;
}

export const DetailsStep: FC<DetailsStepProps> = ({
    customer,
    onSubmit,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const [tabs, setTabs] = useState<Tab[]>([]);
    const [activeTab, storeActiveTab] = useSessionStorage<CheckoutDetailsTab>(checkoutDetailsTabKey, CheckoutDetailsTab.existingCustomer);

    useEffect((): void => {
        const existingCustomerTab = {
            label: trans('containers.checkout.detailsStep.existingCustomer'),
            value: CheckoutDetailsTab.existingCustomer,
        };

        const guestTab = {
            label: trans('containers.checkout.detailsStep.guest'),
            value: CheckoutDetailsTab.guest,
        };

        if (customer) {
            storeActiveTab(CheckoutDetailsTab.existingCustomer);

            setTabs([existingCustomerTab]);
            return;
        }

        setTabs([existingCustomerTab, guestTab]);
    }, [customer]);

    const handleOrdersTabSelect = (value: string): void => {
        const selectedTab = value as CheckoutDetailsTab;

        storeActiveTab(selectedTab);
    };

    return (
        <div className={`details-step ${className}`}>
            <h2 className="details-step__heading">
                {trans('containers.checkout.detailsStep.heading')}
            </h2>

            <TabList
                tabs={tabs}
                activeTab={activeTab}
                onTabSelect={handleOrdersTabSelect}
                className="details-step__tabs"
                tabItemLabelClassName="details-step__tabs-item-label"
            />

            {activeTab === CheckoutDetailsTab.existingCustomer && !customer && (
                <ConnectedLoginForm />
            )}

            {activeTab === CheckoutDetailsTab.existingCustomer && customer && (
                <CheckoutDetailsForm
                    customer={customer}
                    onSubmit={onSubmit}
                />
            )}

            {activeTab === CheckoutDetailsTab.guest && (
                <CheckoutDetailsForm onSubmit={onSubmit} />
            )}
        </div>
    );
};
